import React, { useState, useRef, useEffect } from 'react';
import { apiService } from '../services/api';
import { webSocketService } from '../services/websocket';
import BowtieIcon from './BowtieIcon';
import Tooltip from './Tooltip';
import styles from './DocumentUploadForm.module.css';

export interface UploadStage {
  stage: 'uploading' | 'extracting' | 'processing' | 'completed';
  progress: number;
  message: string;
}

interface DocumentUploadFormProps {
  resumeInterviewId: string;
  onUploadComplete?: (message: any) => void;
  onError?: (error: string) => void;
  onUploadStart?: () => void;
  onStageChange?: (stage: UploadStage | null) => void;
}

const DocumentUploadForm: React.FC<DocumentUploadFormProps> = ({
  resumeInterviewId,
  onUploadComplete,
  onError,
  onUploadStart,
  onStageChange
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState<UploadStage | null>(null);
  const [showBowtie, setShowBowtie] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Forward stage changes to parent
  useEffect(() => {
    onStageChange?.(currentStage);
  }, [currentStage, onStageChange]);

  // Listen for WebSocket upload progress messages
  useEffect(() => {
    const handleUploadProgress = (message: any) => {
      if (message.action === 'resumeUploadProgress' && 
          message.resumeInterviewId === resumeInterviewId) {
        setCurrentStage({
          stage: message.stage,
          progress: message.progress,
          message: message.message
        });
        
        // Show bowtie animation for processing stages
        if (message.stage === 'extracting' || message.stage === 'processing') {
          setShowBowtie(true);
        }
      }
    };

    const handleUploadComplete = (message: any) => {
      if (message.action === 'resumeUploadComplete' && 
          message.resumeInterviewId === resumeInterviewId) {
        setShowBowtie(false);
        setIsUploading(false);
        // Don't set stage here - it should already be set by resumeUploadProgress
        // This prevents duplicate stage updates
        // Parent will handle completion timing
        onUploadComplete?.(message);
      }
    };

    const handleUploadError = (message: any) => {
      if (message.action === 'resumeUploadError' && 
          message.resumeInterviewId === resumeInterviewId) {
        setShowBowtie(false);
        setIsUploading(false);
        setCurrentStage(null);
        onStageChange?.(null);
        onError?.(message.error || 'Upload failed');
      }
    };

    const handleToolSelection = (message: any) => {
      if (message.action === 'resumeInterviewToolSelection' && 
          message.resumeInterviewId === resumeInterviewId &&
          message.toolName === 'process_upload') {
        setShowBowtie(true);
      }
    };

    const handleToolExecution = (message: any) => {
      if (message.action === 'resumeInterviewToolExecution' && 
          message.resumeInterviewId === resumeInterviewId &&
          message.toolName === 'process_upload') {
        setShowBowtie(false);
      }
    };

    webSocketService.on('resumeUploadProgress', handleUploadProgress);
    webSocketService.on('resumeUploadComplete', handleUploadComplete);
    webSocketService.on('resumeUploadError', handleUploadError);
    webSocketService.on('resumeInterviewToolSelection', handleToolSelection);
    webSocketService.on('resumeInterviewToolExecution', handleToolExecution);

    return () => {
      webSocketService.off('resumeUploadProgress', handleUploadProgress);
      webSocketService.off('resumeUploadComplete', handleUploadComplete);
      webSocketService.off('resumeUploadError', handleUploadError);
      webSocketService.off('resumeInterviewToolSelection', handleToolSelection);
      webSocketService.off('resumeInterviewToolExecution', handleToolExecution);
    };
  }, [resumeInterviewId, onUploadComplete, onError, onStageChange]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const droppedFile = files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
        // Auto-start upload after file drop
        setTimeout(() => handleUpload(droppedFile), 100);
      } else {
        onError?.('Please upload a PDF file');
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        // Auto-start upload after file selection
        setTimeout(() => handleUpload(selectedFile), 100);
      } else {
        onError?.('Please upload a PDF file');
        e.target.value = '';
      }
    }
  };

  const handleUpload = async (fileToUpload?: File) => {
    const uploadFile = fileToUpload || file;
    if (!uploadFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    
    // Notify parent that upload is starting
    onUploadStart?.();
    
    // Set initial uploading stage
    setCurrentStage({
      stage: 'uploading',
      progress: 0,
      message: 'Uploading file...'
    });

    try {
      // Get presigned URL from backend
      const headers = await apiService.getAuthHeaders();
      const response = await fetch(`${apiService.baseUrl}/upload-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          resumeInterviewId,
          filename: uploadFile.name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl } = await response.json();

      // Upload file to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': 'application/pdf'
        },
        // Track upload progress if supported
        onUploadProgress: (progressEvent: any) => {
          if (progressEvent.lengthComputable) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(progress);
          }
        }
      } as any);

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file');
      }

      // Don't set progress to 100 here - it causes the modal to show wrong stages
      // The backend will send proper progress updates via WebSocket
      setUploadProgress(100);
      // Just indicate upload is done, backend will handle the rest
    } catch (error) {
      console.error('Upload error:', error);
      onError?.(error instanceof Error ? error.message : 'Upload failed');
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={styles.uploadFormContainer}>
      <div className={styles.uploadHeader}>
        <div className={styles.uploadIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="12" y1="18" x2="12" y2="12"/>
            <line x1="9" y1="15" x2="12" y2="12"/>
            <line x1="15" y1="15" x2="12" y2="12"/>
          </svg>
        </div>
        <h3 className={styles.uploadTitle}>Upload Your Resume</h3>
      </div>

      <p className={styles.uploadDescription}>
        Upload your existing resume (PDF format) and I'll convert it to our professional format.
      </p>


      {!file ? (
        <div
          className={`${styles.dropZone} ${isDragging ? styles.dragging : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            onChange={handleFileChange}
            className={styles.fileInput}
          />
          
          <div className={styles.dropZoneContent}>
            {/* <svg className={styles.uploadCloudIcon} width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v9m0 0l-3-3m3 3l3-3"/>
              <path d="M8.5 7a6.5 6.5 0 1 0 13 0c0-.898-.182-1.754-.511-2.53A6 6 0 0 0 9.29 4.03"/>
              <path d="M3 13a4 4 0 0 0 0 8h13a5 5 0 0 0 1.671-9.712"/>
            </svg> */}
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-12" />
              <path d="M12 16V10m-3 3 3-3 3 3" />
            </svg>


            
            <p className={styles.dropZoneText}>
              <span className={styles.dropZoneTextPrimary}>Click to upload</span> or drag and drop
            </p>
            <p className={styles.dropZoneSubtext}>PDF files only (max 10MB)</p>
          </div>
        </div>
      ) : (
        <div className={styles.filePreview}>
          <div className={styles.fileInfo}>
            <svg className={styles.fileIcon} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            <div className={styles.fileDetails}>
              <p className={styles.fileName}>{file.name}</p>
              <p className={styles.fileSize}>{(file.size / 1024 / 1024).toFixed(2)} MB</p>
            </div>
            {!isUploading && (
              <Tooltip content="Remove file">
                <button
                  type="button"
                  onClick={handleRemove}
                  className={styles.removeButton}
                  aria-label="Remove file"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      )}

      {!file && (
        <Tooltip content="Select a document to upload">
          <button
            type="button"
            className={styles.uploadButton}
            onClick={() => fileInputRef.current?.click()}
            aria-label="Choose file to upload"
          >
            Choose File
          </button>
        </Tooltip>
      )}
    </div>
  );
};

export default DocumentUploadForm;